/* eslint-disable @shopliftr/angular/request-service-naming -- Authentication Service follow its own pattern */
import {
    BehaviorSubject, EMPTY, interval, Observable,
    throwError
} from "rxjs";

import {
    catchError,
    debounce,
    distinctUntilChanged,
    filter,
    map,
    pairwise,
    tap
} from "rxjs/operators";

import {
    DestroyRef,
    Injectable,
    OnDestroy,
    inject
} from "@angular/core";

import { HttpClient } from "@angular/common/http";

import { User } from "@shopliftr/common-js/shared";

import { AuthenticationToken } from "../models/authentication-token";

import { INotification } from "../../notification/interfaces/notification";

import { ConfigurationException } from "../../exceptions/configuration-exception";
import { ExpiredTokenException } from "../../exceptions/expired-token-exception";
import { InvalidTokenException } from "../../exceptions/invalid-token-exception";
import { MissingTokenException } from "../../exceptions/missing-token-exception";

import {
    IAPI_APP_ROUTER_SERVICE, INTERNAL_API_APP_CONFIG
} from "../constants/iapi-app-tokens";
import {
    EXPIRED_TEMPORARY_TOKEN_MESSAGE,
    EXPIRED_TOKEN_MESSAGE, INVALID_TEMPORARY_TOKEN_MESSAGE, INVALID_TOKEN_MESSAGE
} from "../constants/iapi-app-constants";
import { StringUtil } from "@shopliftr/common-js/data";
import { InvalidParametersException } from "../../exceptions/invalid-parameters-exception";
import { InvalidLoginException } from "../../exceptions/invalid-login-exception";
import { COMMON_ERROR_HANDLER } from "../../exceptions/constants/exception-constants";
import { UserRequestService } from "./request/user/user-request.service";
import { ServiceException } from "../../exceptions/service-exception";
import { NotificationType } from "../../notification/enums/notification-type.enum";
import { NavigationSource } from "../../application/enums/navigation-source";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NotificationService } from "../../notification/services/notification.service";

/**
 * The specialized service used to handle all authentication actions
 *
 * It handles state of the authentication components
 */
@Injectable({
    providedIn: "root"
})
export class AuthenticationService implements OnDestroy {

    /**
     * The email address entered into the forms
     */
    email?: string;

    /**
     * An observable to the AuthenticationToken
     *
     * If the user is not logged in it will be undefined
     */
    get authToken$(): Observable<AuthenticationToken> {

        return this._authToken$
            .asObservable()
            .pipe(
                // Only emit an event when token has actually changed
                distinctUntilChanged(undefined, (token) => token?.jwtToken)
            );
    }

    /**
     * An observable that will emit whenever there is a valid auth token
     *
     * If the user is not logged in, the token is temporary or is temporary it will not emit
     */
    get validToken$(): Observable<AuthenticationToken> {
        return this.authToken$.pipe(
            filter((token) => token && !token.isExpired && !token.isTemporary)
        );
    }

    /**
     * the current AuthenticationToken
     *
     * If the user is not logged in it will be undefined
     */
    get authToken(): AuthenticationToken {

        return this._authToken$.value;
    }

    /**
     * An observable for the authenticated user
     * Will be undefined if no user is authenticated.
     */
    get authenticatedUser$(): Observable<User> {

        return this.authToken$.pipe(map((token) => token?.user));
    }

    /**
     * The current authenticated user
     * Will be undefined if no user is authenticated
     */
    get authenticatedUser(): User {

        return this.authToken?.user;
    }

    /**
     * Emits an observable every time the user logs out
     */
    get onLogout$(): Observable<void> {

        return this.authToken$.pipe(
            pairwise(),
            filter(([prev, cur]) => !!(prev && !cur)),
            map(() => {})
        );
    }

    /**
     * Emits an observable with the authenticated user every time a user is logged in
     */
    get onLogin$(): Observable<User> {

        return this.authToken$.pipe(
            pairwise(),
            filter(([prev, cur]) => !!(!prev && cur)),
            map(([prev, cur]) => cur.user)
        );
    }

    /**
     * The Application Router used to interact with the Router
     */
    private readonly _appRouter = inject(IAPI_APP_ROUTER_SERVICE);

    /**
     * Manages the state of the Authentication token
     */
    private readonly _authToken$ = new BehaviorSubject<AuthenticationToken>(undefined);

    /**
     * The URL used to make login requests to internal api
     */
    private readonly _iapiLoginUrl: string;

    /**
     * HttpClient used to contact the login endpoint on Internal Api
     */
    private readonly _httpClient = inject(HttpClient);

    /**
     * The ApplicationConfig used to generate the _iapiLoginUrl
     */
    private readonly _appConfig = inject(INTERNAL_API_APP_CONFIG);

    /**
     * Error handler used to handle any error that may arise
     */
    private readonly _errorHandler = inject(COMMON_ERROR_HANDLER);

    /**
     * User service used to set the user's password
     */
    private readonly _userService = inject(UserRequestService);

    /**
     * Service used to display notifications
     */
    private readonly _notificationService = inject(NotificationService);

    /**
     * The reference for when this service is destroyed
     */
    private readonly _destroyRef = inject(DestroyRef);

    constructor() {

        if (!this._appConfig.authentication) {
            throw new ConfigurationException("AuthService requires the 'authentication' environment configuration.");
        }

        this._iapiLoginUrl = `${this._appConfig.apiUrl}/v1/user/login`;

        this._initAuthTokenPersistence();
        this._initTokenExpiryHandler();

    }

    ngOnDestroy(): void {

        this._authToken$.complete();
    }

    /**
     * Authenticate user
     */
    login$(email: string, password: string): Observable<void> {

        const body = {
            email: email,
            password: password
        };
        this.email = email;
        return this._httpClient.post(this._iapiLoginUrl, body).pipe(
            // The token is set automatically by the InternalApiInterceptor
            // This just has the observable emit void
            map(() => {}),
            catchError((error: unknown) => {
                if (error instanceof InvalidLoginException) {
                    return throwError(() => error);
                }
                const message = "Error on login";
                this._errorHandler.handleServiceError(error, message);
                return throwError(() => new ServiceException(`${message}: ${(error as Error).message}`, error));
            }),
            tap({
                subscribe: () => {
                    // Ensure that the auth token is before starting this
                    this._clearAuthToken();
                },
                finalize: () => {
                    if (this.authToken) {
                        // After the subscription is done redirect to post login
                        this._appRouter.toPostLogin();
                    }
                }
            })
        );
    }

    forgotPasswordRequest$(email: string): Observable<void> {

        if (StringUtil.emptyString(email, true)) {
            return throwError(() => new InvalidParametersException("Password is required"));
        }
        return this._requestForgotPasswordRequest$(email, {
            type: NotificationType.Success,
            message: "An email with password reset instructions should arrive in your inbox in the next few minutes.",
            persistent: true
        });
    }

    setPassword$(password: string): Observable<void> {

        if (StringUtil.emptyString(password, true)) {
            return throwError(() => new InvalidParametersException("Password is required"));
        }

        if (!this._appRouter.temporaryToken) {
            // This should never happen but just in case
            this._errorHandler.handleServiceError(new MissingTokenException(), "setPassword on AuthenticationService was called when the appRouter had no temporaryToken");
            this.handleInvalidTemporaryToken();
            return EMPTY;
        }
        if (this._appRouter.temporaryToken.isExpired) {
            this.handleExpiredTemporaryToken(this._appRouter.temporaryToken);
            return EMPTY;
        }

        return this._userService.setPassword$(password, undefined, this._appRouter.temporaryToken).pipe(
            catchError((error: unknown) => {
                const message = "Failed to set user password from temporary token";
                this._errorHandler.handleServiceError(error, message);
                return throwError(() => new ServiceException(message, error));
            }),
            map(() => {}),
            tap({
                subscribe: () => {
                    // Ensure that the auth token is cleared
                    this._clearAuthToken();
                },
                finalize: () => {
                    if (this.authToken) {
                        // The operation was successful
                        this._appRouter.toPostLogin();
                    }
                }
            })
        );
    }

    /**
     * Sets the state of the token to logged out and fires the appropriate events
     * Adds an expired token error message
     * This function does not redirect the router that is handled by the caller
     * As it could be a guard or the logout redirect function
     */
    handleExpiredToken(): void {

        this.handleLogout({
            type: NotificationType.Error,
            message: EXPIRED_TOKEN_MESSAGE,
            persistent: true,
            action: "OK"
        });
    }

    handleExpiredTemporaryToken(token: AuthenticationToken): void {

        if (!token?.isTemporary || !token?.email) {
            this.handleInvalidTemporaryToken();
        }

        this._requestForgotPasswordRequest$(token.email, {
            type: NotificationType.Error,
            message: EXPIRED_TEMPORARY_TOKEN_MESSAGE,
            persistent: true,
            action: "OK"
        }).pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({
                error: (error: unknown) => {
                    this._errorHandler.handleServiceError(error, `Failed to re-request temporary token for ${token.email}`);
                    this.handleInvalidTemporaryToken();
                }
            });
    }

    handleInvalidTemporaryToken(): void {

        this.handleLogout({
            type: NotificationType.Error,
            message: INVALID_TEMPORARY_TOKEN_MESSAGE,
            persistent: true,
            action: "OK"
        });
    }

    /**
     * Sets the state of the token to logged out and fires the appropriate events
     * Adds an invalid token error message, either the one provided or a default message
     *
     * This function does not redirect the router that is handled by the caller
     */
    handleInvalidToken(): void {

        this.handleLogout({
            type: NotificationType.Error,
            message: INVALID_TOKEN_MESSAGE,
            persistent: true,
            action: "OK"
        });
    }

    /**
     * Navigate to the forgot password request and populate with the given email
     */
    toForgotPasswordRequest(email?: string): void {

        this.email = email;
        this._appRouter.toForgotPasswordRequest();
    }

    /**
     *
     * Sets the state of the token to logged out and fires the appropriate events
     * This function does not redirect the router that is handled by the caller
     */
    handleLogout(message?: INotification): void {

        this.email = this.authenticatedUser?.email;
        // set or clear message displayed to the login screen
        this._notificationService.dismissAll();
        this._notificationService.displayNotification(message);
        // clear the token
        this._clearAuthToken();
    }

    /**
     * Updates the current Auth Token from an http request header
     *
     * This function is called by the InternalApiInterceptor with the value from the authentication header of a http response
     *
     * If the token provided is invalid an exception will be thrown.
     *
     * The Interceptor has logic that handles the token related exceptions
     */
    updateAuthToken(jwtToken: string): void {

        try {
            this._updateAuthToken(jwtToken);
        }
        catch (error) {

            this._errorHandler.handleServiceError(error, "Failed to update auth token on authentication service");
            throw error;
        }
    }

    /**
     * This ensures that the value of the authToken is persisted to local storage whenever it is updated
     *
     * This function should only be called by the constructor
     *
     * When called it will update the auth token to any persisted token if its valid
     *
     * In the case that the persisted token is invalid or expired,
     * it will navigate to the login page with an appropriate message displayed to the user
     */
    private _initAuthTokenPersistence(): void {

        // Retrieve any persisted token from localStorage
        const jwtToken = localStorage.getItem("authToken");
        if (jwtToken) {
            try {
                this._updateAuthToken(jwtToken);
            }
            catch (error) {
                if (error instanceof ExpiredTokenException) {
                    this.handleExpiredToken();
                }
                else {
                    this.handleInvalidToken();
                }
                this._errorHandler.handleTokenError(error, "Failed decode persisted jwt token");
                this._appRouter.toLogin();
            }
        }

        this.authToken$.pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe((token) => {

                if (token?.jwtToken) {

                    localStorage.setItem("authToken", token.jwtToken);
                }
                else {
                    localStorage.removeItem("authToken");
                }
            });
    }

    /**
     * Listen for token changes and if they expire before they are renewed redirect to the login page
     */
    private _initTokenExpiryHandler(): void {

        this.authToken$.pipe(
            // Create a debounce for the ttl of the token
            // If the token is replaced the debounce will terminate
            // and a new one takes it place
            // if the token is undefined if emits immediately
            debounce((token) => interval(token?.ttl ?? 0)),
            // Filter out undefined tokens
            filter((token) => !!token),
            takeUntilDestroyed(this._destroyRef)
        ).subscribe(() => {
            // This is only called when the token's ttl has expired
            this.handleExpiredToken();
            this._appRouter.toLogin(NavigationSource.Error);
        });
    }

    /**
     * This function persists the auth token
     *
     * This function will throw exceptions if the token is in any way invalid
     *
     * This function does not handle the errors in any way
     */
    private _updateAuthToken(jwtToken: string): void {
        if (jwtToken) {
            const token = new AuthenticationToken(jwtToken);
            if (token.isTemporary) {
                throw new InvalidTokenException("Auth Service cannot use a temporary token.");
            }
            if (token.isExpired) {
                throw new ExpiredTokenException();
            }

            this._authToken$.next(token);
        }
        else {
            throw new MissingTokenException();
        }
    }

    /**
     * Clears the _authToken$ if it is set
     */
    private _clearAuthToken(): void {

        if (this.authToken) {
            this._authToken$.next(undefined);
        }
    }

    private _requestForgotPasswordRequest$(email: string, message: INotification): Observable<void> {

        let error = false;
        return this._userService.forgotPassword$(email).pipe(
            catchError((error: unknown) => {
                const message = `Failed to request password reset for ${email}`;
                this._errorHandler.handleServiceError(error, message);
                return throwError(() => new ServiceException(message, error));
            }),
            map(() => {}),
            tap({
                finalize: () => {
                    if (!error) {
                        this._notificationService.displayNotification(message);
                        this._appRouter.toLogin();
                    }
                }
            })
        );
    }
}
